import { ShoppingBag } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Button } from "../../components/ui/button";

const reais = (v) => `R$ ${Number(v || 0).toFixed(2).replace(".", ",")}`;

// Sacola do cardápio no computador: cartão ao lado dos produtos que acompanha a
// rolagem (decisão do dono, 01/10/2026 — "cartão que acompanha a rolagem",
// sempre visível, mesmo vazio). No celular a sacola segue no rodapé + popup.
//
// É só a moldura: a página monta o conteúdo e passa pronto —
//   topo       → ofertas/cupom e carteira de cashback
//   children   → os itens (ItemSacola)
//   sugestoes  → "Combina com…" (features/sugestoes, que esta feature não importa)
//   cupom      → a linha "Tem um cupom?" (CupomDaSacola), quando a loja tem cupom
// Embaixo, sempre à vista (24/10/2026, dono: "visivelmente mais organizado"):
// Subtotal, Taxa de entrega, desconto do cupom escolhido, Total e o botão —
// apagado e escrito "Sacola vazia" enquanto não tem item.
// `distanciaTopo` é onde o cartão gruda: abaixo da barra do topo, da busca
// quando aberta e da barra de categorias, que no computador fica acima da
// sacola, na largura toda (02/10/2026). A altura máxima desconta isso para o
// botão nunca sumir. `mt-2` alinha o cartão com o título da 1ª categoria.
export default function SacolaLateral({
  t, quantidade, total, rotuloTotal = "Total", mostrarEntrega = true, desconto = null, cupom = null,
  rotuloBotao, onFinalizar, topo, sugestoes, children, distanciaTopo = 56,
}) {
  const vazia = quantidade === 0;
  const totalComDesconto = Math.max(0, total - (desconto?.valor || 0));
  const pulsando = usePulsoAoAdicionar(quantidade);
  return (
    <aside aria-label="Sacola" data-e2e="sacola-lateral" className="hidden lg:block sticky self-start mt-2" style={{ top: distanciaTopo }}>
      <div
        className={`flex flex-col rounded-2xl border ${t.border} ${t.bgCard} overflow-hidden shadow-sm transition-shadow duration-300 ${pulsando ? "ring-2 ring-orange-500/70 shadow-lg shadow-orange-500/20" : ""}`}
        style={{ maxHeight: `calc(100vh - ${distanciaTopo + 16}px)` }}
      >
        <div className={`px-4 py-3 border-b ${t.border} flex items-center gap-2 flex-shrink-0`}>
          <ShoppingBag className="w-5 h-5 text-orange-500" />
          <h2 className={`font-bold text-sm ${t.text}`}>Sua sacola</h2>
          {!vazia && (
            <span className="ml-auto text-[11px] font-bold px-2 py-0.5 rounded-full bg-orange-500 text-white tabular-nums" data-e2e="sacola-lateral-qtd">
              {quantidade} {quantidade === 1 ? "item" : "itens"}
            </span>
          )}
        </div>

        {topo && <div className="flex-shrink-0">{topo}</div>}

        <div className="flex-1 min-h-0 overflow-y-auto p-2">
          {vazia ? <SacolaVazia t={t} /> : <div className="space-y-2">{children}</div>}
        </div>

        <div className={`p-3 border-t ${t.border} ${t.bgCartItem} flex-shrink-0 space-y-3`}>
          {!vazia && sugestoes}
          <div className="space-y-1 text-sm" data-e2e="sacola-lateral-resumo">
            <Linha t={t} rotulo="Subtotal" valor={reais(total)} />
            {mostrarEntrega && <Linha t={t} rotulo="Taxa de entrega" valor="a calcular" mudo />}
            {desconto && !vazia && (
              <Linha t={t} rotulo={desconto.rotulo} valor={desconto.freteGratis ? "Frete grátis" : `− ${reais(desconto.valor)}`} verde />
            )}
            <div className={`flex items-baseline justify-between pt-2 mt-1 border-t ${t.border}`}>
              <span className={`font-bold ${t.text}`}>{rotuloTotal}</span>
              <span className={`text-lg font-bold tabular-nums ${vazia ? t.textMuted : "text-orange-500"}`} data-e2e="sacola-lateral-total">{reais(totalComDesconto)}</span>
            </div>
          </div>
          {cupom}
          <Button onClick={onFinalizar} disabled={vazia}
            className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold h-11 text-sm rounded-xl disabled:opacity-50">
            {vazia ? "Sacola vazia" : rotuloBotao}
          </Button>
        </div>
      </div>
    </aside>
  );
}

function Linha({ t, rotulo, valor, mudo, verde }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className={verde ? "text-emerald-600 dark:text-emerald-400" : t.textMuted}>{rotulo}</span>
      <span className={`tabular-nums ${verde ? "text-emerald-600 dark:text-emerald-400 font-semibold" : mudo ? `${t.textMuted2} text-xs` : t.text}`}>{valor}</span>
    </div>
  );
}

function SacolaVazia({ t }) {
  return (
    <div className="flex flex-col items-center justify-center py-8 px-4 text-center" data-e2e="sacola-lateral-vazia">
      <ShoppingBag className={`w-10 h-10 ${t.textMuted2} mb-3`} strokeWidth={1.5} />
      <p className={`text-sm font-semibold ${t.text}`}>Sua sacola está vazia</p>
      <p className={`text-xs ${t.textMuted} mt-1`}>Escolha um produto no cardápio e ele aparece aqui.</p>
    </div>
  );
}

// Anel laranja por um instante quando entra item: no computador a sacola não
// abre sozinha como o popup do celular, então é o sinal de "foi pra sacola".
function usePulsoAoAdicionar(quantidade) {
  const [pulsando, setPulsando] = useState(false);
  const anterior = useRef(quantidade);
  useEffect(() => {
    const subiu = quantidade > anterior.current;
    anterior.current = quantidade;
    // Sem isso, tirar um item no meio do pulso cancelava o timer e o anel ficava aceso.
    if (!subiu) { setPulsando(false); return undefined; }
    setPulsando(true);
    const timer = setTimeout(() => setPulsando(false), 700);
    return () => clearTimeout(timer);
  }, [quantidade]);
  return pulsando;
}
